<script setup lang="ts">
import Header from '@/components/Common/Header.vue'
import MobileHeader from '@/components/Common/MobileHeader.vue'
import { watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import i18n from './i18n'
import { useLangStore } from '@/pinia/stores/lang'
import { buildRouteSchemas } from '@/common/seo/structuredData'

const langStore = useLangStore()
const route = useRoute()

const localeByPathSegment = {
  zh: 'zh-CN',
  en: 'en-US'
} as const

const pathSegmentByLocale = {
  'zh-CN': 'zh',
  'en-US': 'en'
} as const

type SupportedLocale = keyof typeof pathSegmentByLocale

const storageKey = 'site_lang'
const siteOrigin = (import.meta.env.VITE_SITE_ORIGIN as string | undefined)?.replace(/\/$/, '')
const defaultSiteOrigin = siteOrigin || 'https://www.shsctec.com'
const localePathPattern = /^\/(zh|en)(?=\/|$)/i
const schemaScriptSelector = 'script[type="application/ld+json"][data-schema="global"]'

const companyNameByLocale: Record<SupportedLocale, string> = {
  'zh-CN': '上海超导技术有限公司',
  'en-US': 'Shanghai Superconductor Technology Co., Ltd.'
}

const siteNameByLocale: Record<SupportedLocale, string> = {
  'zh-CN': '上海超导官网',
  'en-US': 'Shanghai Superconductor Official Website'
}

const homeLabelByLocale: Record<SupportedLocale, string> = {
  'zh-CN': '首页',
  'en-US': 'Home'
}

const siteDescriptionByLocale: Record<SupportedLocale, string> = {
  'zh-CN':
    '上海超导是一家专注于第二代高温超导（REBCO）带材研发、生产和销售的高新技术企业，年产能达4000km（12mm），产品广泛应用于可控核聚变、超导电力、高场磁体等前沿领域，是全球高温超导带材核心供应商之一。',
  'en-US':
    'Shanghai Superconductor is a high-tech enterprise focused on the R&D, production and sales of 2G high-temperature superconducting (REBCO) tapes, with an annual capacity of 4,000 km (12mm), serving controlled nuclear fusion, superconducting power and high-field magnet applications. It is one of the world\'s core HTS tape suppliers.'
}

const defaultKeywordsByLocale: Record<SupportedLocale, string[]> = {
  'zh-CN': ['上海超导', '高温超导', '超导带材', 'REBCO', '可控核聚变', '超导电力', '高场磁体'],
  'en-US': [
    'Shanghai Superconductor',
    'high temperature superconductor',
    'HTS tape',
    'REBCO',
    'fusion',
    'superconducting power',
    'high-field magnet'
  ]
}

const pageTitleByLocale: Record<SupportedLocale, Record<string, string>> = {
  'zh-CN': {
    '/': '第二代高温超导带材（REBCO）制造商',
    '/company': '关于公司',
    '/company/introduction': '公司介绍',
    '/company/dream': '愿景和使命',
    '/company/partners': '合作伙伴',
    '/company/honors': '荣誉资质',
    '/company/events': '公司大事记',
    '/company/structure': '下设机构',
    '/company/structure/center': '上海超导材料及系统工程技术研究中心',
    '/company/structure/lab': '上海市高温超导材料与应用技术重点实验室',
    '/company/structure/tech': '合肥埃聚磁性科技有限公司',
    '/company/structure/materials': '合肥上超材料科技有限公司',
    '/applications': '应用场景',
    '/applications/magnetic': '高场磁体',
    '/applications/power': '超导电力',
    '/applications/nuclear': '可控核聚变',
    '/products': '产品与技术服务',
    '/products/tape': '超导带材',
    '/products/performance': '性能参数',
    '/products/advantages': '核心优势',
    '/products/advantages/technology': '技术、工艺',
    '/products/advantages/selfequipped': '自主装备',
    '/products/advantages/qualitycontrol': '质量控制',
    '/products/advantages/deliverycapability': '交付能力',
    '/products/service': '技术服务',
    '/news': '公司动态',
    '/news/company': '公司动态',
    '/news/downloads': '资料下载',
    '/contact': '联系我们',
    '/contact/postdoc': '博士后分站',
    '/contact/info': '联系方式',
    '/recruit': '招聘'
  },
  'en-US': {
    '/': '2G HTS Tape (REBCO) Manufacturer',
    '/company': 'About Us',
    '/company/introduction': 'Company Profile',
    '/company/dream': 'Vision & Mission',
    '/company/partners': 'Partners',
    '/company/honors': 'Honors & Qualifications',
    '/company/events': 'Memorabilia',
    '/company/structure': 'Subsidiary Organizations',
    '/company/structure/center': 'Shanghai Superconductor Materials and System Engineering Technology Research Center',
    '/company/structure/lab': 'Shanghai Key Laboratory of High Temperature Superconductor Materials and Applications',
    '/company/structure/tech': 'Hefei Aiju Magnetic Technology Co., Ltd.',
    '/company/structure/materials': 'Hefei Shangchao Materials Technology Co., Ltd.',
    '/applications': 'Applications',
    '/applications/magnetic': 'High-Field Magnets',
    '/applications/power': 'Superconducting Power',
    '/applications/nuclear': 'Controlled Nuclear Fusion',
    '/products': 'Products & Services',
    '/products/tape': 'HTS Tapes',
    '/products/performance': 'Performance',
    '/products/advantages': 'Core Advantages',
    '/products/advantages/technology': 'Technology',
    '/products/advantages/selfequipped': 'Self-Developed Equipment',
    '/products/advantages/qualitycontrol': 'Quality Control',
    '/products/advantages/deliverycapability': 'Delivery Capability',
    '/products/service': 'Technical Services',
    '/news': 'Company News',
    '/news/company': 'Company News',
    '/news/downloads': 'Downloads',
    '/contact': 'Contact Us',
    '/contact/postdoc': 'Postdoctoral Station',
    '/contact/info': 'Contact Information',
    '/recruit': 'Recruitment'
  }
}

const pageDescriptionByLocale: Record<SupportedLocale, Record<string, string>> = {
  'zh-CN': {
    '/': '上海超导官网。上海超导是全球第二代高温超导（REBCO）带材的核心供应商之一，专注高温超导材料研发、生产与销售，产品应用于可控核聚变、超导电力、高场磁体等前沿领域。业务咨询：sales@shsctec.com，电话 021-64056173。',
    '/company': '了解上海超导的发展历程、公司介绍、愿景使命、合作伙伴、荣誉资质及下设机构。',
    '/company/introduction': '查看上海超导公司介绍，了解企业定位、核心业务与高温超导材料产业化能力。',
    '/company/dream': '查看上海超导的愿景和使命，了解公司在高温超导领域的发展方向与价值追求。',
    '/company/partners': '了解上海超导与科研院所、高校及产业伙伴的合作生态与典型合作伙伴。',
    '/company/honors': '查看上海超导获得的荣誉资质、创新奖项及企业认定信息。',
    '/company/events': '浏览上海超导公司大事记，了解企业重要里程碑与阶段性成果。',
    '/company/structure': '查看上海超导下设机构与平台布局，了解研发与产业协同体系。',
    '/applications': '了解上海超导高温超导带材在可控核聚变、超导电力和高场磁体等领域的应用场景。',
    '/applications/magnetic': '查看上海超导高温超导带材在高场磁体方向的应用案例与技术能力。',
    '/applications/power': '查看上海超导高温超导带材在超导电力方向的应用案例与技术能力。',
    '/applications/nuclear': '查看上海超导高温超导带材在可控核聚变方向的应用案例与技术能力。',
    '/products': '了解上海超导的产品与技术服务，涵盖超导带材、性能参数、核心优势与配套服务。',
    '/products/tape': '查看上海超导第二代高温超导带材产品信息，了解规格、性能及应用方向。',
    '/products/performance': '查看上海超导高温超导带材的关键性能参数与产品能力。',
    '/products/advantages': '了解上海超导在技术工艺、自主装备、质量控制和交付能力方面的核心优势。',
    '/products/advantages/technology': '查看上海超导在高温超导带材制备方面的核心技术与工艺能力。',
    '/products/advantages/selfequipped': '了解上海超导自主装备能力，查看关键设备与产线支撑体系。',
    '/products/advantages/qualitycontrol': '查看上海超导质量控制体系，了解产品过程检验与出厂检测能力。',
    '/products/advantages/deliverycapability': '了解上海超导在高温超导带材领域的批量交付与定制交付能力。',
    '/products/service': '了解上海超导围绕高温超导带材提供的技术支持与技术服务能力。',
    '/news': '浏览上海超导公司动态、行业资讯与技术进展，了解最新新闻与更新。',
    '/news/company': '查看上海超导公司新闻，获取企业动态、合作进展与技术突破。',
    '/news/downloads': '查看上海超导相关资料下载与研究成果，了解论文、文献与技术资料。',
    '/contact': '联系上海超导，获取公司联络方式、博士后分站信息与业务咨询入口。',
    '/contact/postdoc': '查看上海超导博士后分站信息，了解研究方向与人才引进内容。',
    '/contact/info': '查看上海超导联系方式，包括地址、电话与业务、招聘、咨询邮箱。',
    '/recruit': '查看上海超导招聘信息，了解岗位需求、任职要求与加入方式。'
  },
  'en-US': {
    '/': 'Official website of Shanghai Superconductor, one of the world\'s core suppliers of 2G high-temperature superconducting (REBCO) tapes, serving fusion, superconducting power and high-field magnet applications. Contact: sales@shsctec.com, +86-21-64056173.',
    '/company': 'Learn about Shanghai Superconductor, including company profile, vision and mission, partners, honors and organizational structure.',
    '/company/introduction': 'View the company profile of Shanghai Superconductor and learn about its business focus and industrial capabilities in high-temperature superconducting materials.',
    '/company/dream': 'Explore the vision and mission of Shanghai Superconductor and its long-term direction in the superconducting industry.',
    '/company/partners': 'Discover the partner ecosystem of Shanghai Superconductor across research institutes, universities and industrial collaborators.',
    '/company/honors': 'View the honors, qualifications and recognitions received by Shanghai Superconductor.',
    '/company/events': 'Browse the milestones and major events in the development of Shanghai Superconductor.',
    '/company/structure': 'View the organizational structure and innovation platforms of Shanghai Superconductor.',
    '/applications': 'Explore how Shanghai Superconductor HTS tapes are used in fusion, superconducting power and high-field magnet applications.',
    '/applications/magnetic': 'View high-field magnet application cases and technical capabilities of Shanghai Superconductor HTS tapes.',
    '/applications/power': 'View superconducting power application cases and technical capabilities of Shanghai Superconductor HTS tapes.',
    '/applications/nuclear': 'View controlled nuclear fusion application cases and technical capabilities of Shanghai Superconductor HTS tapes.',
    '/products': 'Explore products and technical services from Shanghai Superconductor, including HTS tapes, performance data, core advantages and support services.',
    '/products/tape': 'View Shanghai Superconductor 2G high-temperature superconducting tape products, specifications and application directions.',
    '/products/performance': 'Review key performance parameters and product capabilities of Shanghai Superconductor HTS tapes.',
    '/products/advantages': 'Learn about Shanghai Superconductor core strengths in technology, self-developed equipment, quality control and delivery capability.',
    '/products/advantages/technology': 'Explore the core technology and process capabilities behind Shanghai Superconductor HTS tape manufacturing.',
    '/products/advantages/selfequipped': 'Learn about Shanghai Superconductor self-developed equipment and production line support capabilities.',
    '/products/advantages/qualitycontrol': 'Review the quality control system and inspection capabilities of Shanghai Superconductor.',
    '/products/advantages/deliverycapability': 'Learn about the batch production and delivery capability of Shanghai Superconductor in the HTS tape field.',
    '/products/service': 'Explore the technical support and service capabilities provided by Shanghai Superconductor.',
    '/news': 'Browse company news, industry updates and technology progress from Shanghai Superconductor.',
    '/news/company': 'Read company news from Shanghai Superconductor, including business progress, collaborations and technical achievements.',
    '/news/downloads': 'Access related publications, downloadable materials and technical references from Shanghai Superconductor.',
    '/contact': 'Contact Shanghai Superconductor for business inquiries, contact information and postdoctoral station details.',
    '/contact/postdoc': 'View postdoctoral station information and research opportunities at Shanghai Superconductor.',
    '/contact/info': 'View the address, phone numbers and email contacts of Shanghai Superconductor.',
    '/recruit': 'View recruitment information, job openings and career opportunities at Shanghai Superconductor.'
  }
}

const pageKeywordsByLocale: Record<SupportedLocale, Record<string, string[]>> = {
  'zh-CN': {
    '/': ['上海超导官网', '高温超导材料', '超导带材'],
    '/company': ['公司介绍', '企业概况', '上海超导技术有限公司'],
    '/company/introduction': ['公司介绍', '企业简介', '高温超导企业'],
    '/company/dream': ['愿景和使命', '企业文化', '发展战略'],
    '/company/partners': ['合作伙伴', '产学研合作', '科研合作'],
    '/company/honors': ['荣誉资质', '企业荣誉', '创新奖项'],
    '/company/events': ['公司大事记', '发展历程', '企业里程碑'],
    '/company/structure': ['下设机构', '研发平台', '组织架构'],
    '/applications': ['应用场景', '超导应用', '行业应用'],
    '/applications/magnetic': ['高场磁体', '超导磁体', '磁体应用'],
    '/applications/power': ['超导电力', '超导电缆', '限流器'],
    '/applications/nuclear': ['可控核聚变', '聚变磁体', '核聚变应用'],
    '/products': ['产品与技术服务', '超导产品', '技术服务'],
    '/products/tape': ['超导带材', '第二代高温超导带材', 'REBCO带材'],
    '/products/performance': ['性能参数', '临界电流', '带材性能'],
    '/products/advantages': ['核心优势', '技术工艺', '交付能力'],
    '/products/advantages/technology': ['技术工艺', '制备工艺', '超导带材工艺'],
    '/products/advantages/selfequipped': ['自主装备', '产线装备', '设备能力'],
    '/products/advantages/qualitycontrol': ['质量控制', '检测能力', '质量体系'],
    '/products/advantages/deliverycapability': ['交付能力', '规模化生产', '批量交付'],
    '/products/service': ['技术服务', '应用支持', '客户服务'],
    '/news': ['公司动态', '行业资讯', '技术进展'],
    '/news/company': ['公司新闻', '企业动态', '新闻中心'],
    '/news/downloads': ['资料下载', '论文文献', '技术资料'],
    '/contact': ['联系我们', '联系方式', '业务咨询'],
    '/contact/postdoc': ['博士后分站', '人才招聘', '科研岗位'],
    '/contact/info': ['地址电话', '业务邮箱', '联系方式'],
    '/recruit': ['招聘', '加入我们', '职位信息']
  },
  'en-US': {
    '/': ['Shanghai Superconductor official website', 'high temperature superconductor materials', 'HTS tape'],
    '/company': ['about Shanghai Superconductor', 'company profile', 'superconductor company'],
    '/company/introduction': ['company profile', 'business overview', 'HTS enterprise'],
    '/company/dream': ['vision and mission', 'company culture', 'strategy'],
    '/company/partners': ['partners', 'industry collaboration', 'research collaboration'],
    '/company/honors': ['honors', 'qualifications', 'awards'],
    '/company/events': ['milestones', 'company events', 'development history'],
    '/company/structure': ['organization structure', 'innovation platform', 'subsidiaries'],
    '/applications': ['applications', 'HTS applications', 'industry solutions'],
    '/applications/magnetic': ['high-field magnets', 'superconducting magnet', 'magnet applications'],
    '/applications/power': ['superconducting power', 'HTS cable', 'fault current limiter'],
    '/applications/nuclear': ['controlled nuclear fusion', 'fusion magnet', 'fusion applications'],
    '/products': ['products and services', 'HTS products', 'technical services'],
    '/products/tape': ['HTS tape', '2G HTS tape', 'REBCO tape'],
    '/products/performance': ['performance', 'critical current', 'tape performance'],
    '/products/advantages': ['core advantages', 'technology', 'delivery capability'],
    '/products/advantages/technology': ['technology', 'manufacturing process', 'HTS tape process'],
    '/products/advantages/selfequipped': ['self-developed equipment', 'production equipment', 'manufacturing capability'],
    '/products/advantages/qualitycontrol': ['quality control', 'inspection capability', 'quality system'],
    '/products/advantages/deliverycapability': ['delivery capability', 'mass production', 'volume supply'],
    '/products/service': ['technical services', 'application support', 'customer service'],
    '/news': ['company news', 'industry updates', 'technology progress'],
    '/news/company': ['company news', 'corporate updates', 'news center'],
    '/news/downloads': ['downloads', 'publications', 'technical documents'],
    '/contact': ['contact us', 'contact information', 'business inquiry'],
    '/contact/postdoc': ['postdoctoral station', 'research positions', 'talent recruitment'],
    '/contact/info': ['address', 'phone', 'email'],
    '/recruit': ['recruitment', 'careers', 'job openings']
  }
}

const toAbsoluteUrl = (pathname: string) => `${defaultSiteOrigin}${pathname}`

const toBreadcrumbLabel = (segment: string, locale: SupportedLocale) => {
  if (!segment) return pageTitleByLocale[locale]['/']
  const normalized = segment.replace(/[-_]+/g, ' ').trim()
  if (locale === 'zh-CN') {
    return normalized || companyNameByLocale[locale]
  }
  return normalized
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || companyNameByLocale[locale]
}

const getPageTitle = (basePathname: string, locale: SupportedLocale) => {
  const localizedTitles = pageTitleByLocale[locale]
  if (localizedTitles[basePathname]) {
    return localizedTitles[basePathname]
  }

  const segments = basePathname.split('/').filter(Boolean)
  for (let index = segments.length; index > 0; index -= 1) {
    const partialPath = `/${segments.slice(0, index).join('/')}`
    if (localizedTitles[partialPath]) {
      const remainder = segments.slice(index)
      if (remainder.length === 0) {
        return localizedTitles[partialPath]
      }
      return `${localizedTitles[partialPath]} - ${toBreadcrumbLabel(remainder[remainder.length - 1], locale)}`
    }
  }

  return pageTitleByLocale[locale]['/']
}

const dedupeKeywords = (keywords: string[]) => [...new Set(keywords.filter(Boolean))]

const getPageDescription = (basePathname: string, locale: SupportedLocale) => {
  const localizedDescriptions = pageDescriptionByLocale[locale]
  if (localizedDescriptions[basePathname]) {
    return localizedDescriptions[basePathname]
  }

  const segments = basePathname.split('/').filter(Boolean)
  for (let index = segments.length; index > 0; index -= 1) {
    const partialPath = `/${segments.slice(0, index).join('/')}`
    if (localizedDescriptions[partialPath]) {
      const pageTitle = getPageTitle(basePathname, locale)
      return locale === 'zh-CN'
        ? `${pageTitle}，${localizedDescriptions[partialPath]}`
        : `${pageTitle}. ${localizedDescriptions[partialPath]}`
    }
  }

  return siteDescriptionByLocale[locale]
}

const getPageKeywords = (basePathname: string, locale: SupportedLocale) => {
  const localizedKeywords = pageKeywordsByLocale[locale]
  const pageTitle = getPageTitle(basePathname, locale)

  if (localizedKeywords[basePathname]) {
    return dedupeKeywords([pageTitle, ...localizedKeywords[basePathname], ...defaultKeywordsByLocale[locale]])
  }

  const segments = basePathname.split('/').filter(Boolean)
  for (let index = segments.length; index > 0; index -= 1) {
    const partialPath = `/${segments.slice(0, index).join('/')}`
    if (localizedKeywords[partialPath]) {
      const tailLabel = segments[index - 1] ? toBreadcrumbLabel(segments[segments.length - 1], locale) : ''
      return dedupeKeywords([
        pageTitle,
        tailLabel,
        ...localizedKeywords[partialPath],
        ...defaultKeywordsByLocale[locale]
      ])
    }
  }

  return dedupeKeywords([pageTitle, ...defaultKeywordsByLocale[locale]])
}

const getBreadcrumbItems = (basePathname: string, locale: SupportedLocale) => {
  const localizedTitles = pageTitleByLocale[locale]
  const items = [
    {
      name: homeLabelByLocale[locale],
      item: toAbsoluteUrl(buildLocalizedPathname('/', locale))
    }
  ]

  const segments = basePathname.split('/').filter(Boolean)
  let accumulatedPath = ''

  for (const segment of segments) {
    accumulatedPath += `/${segment}`
    items.push({
      name: localizedTitles[accumulatedPath] ?? toBreadcrumbLabel(segment, locale),
      item: toAbsoluteUrl(buildLocalizedPathname(accumulatedPath, locale))
    })
  }

  return items
}

const upsertHeadMeta = (selector: string, name: string, content: string) => {
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = name
    document.head.appendChild(meta)
  }
  meta.content = content
}

const upsertJsonLd = (payload: unknown) => {
  let script = document.head.querySelector(schemaScriptSelector) as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema', 'global')
    document.head.appendChild(script)
  }

  script.textContent = JSON.stringify(payload)
}

const toSupportedLocale = (lang: string | null | undefined) =>
  typeof lang === 'string' && lang.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'

const normalizePath = (path: string) => {
  const trimmed = path.replace(/\/{2,}/g, '/')
  if (trimmed === '') return '/'
  if (trimmed.length > 1 && trimmed.endsWith('/')) {
    return trimmed.slice(0, -1)
  }
  return trimmed
}

const getLocaleFromPathname = (pathname: string) => {
  const matched = pathname.match(localePathPattern)
  if (!matched) return null
  const segment = matched[1].toLowerCase() as keyof typeof localeByPathSegment
  return localeByPathSegment[segment]
}

const getBasePathname = (pathname: string) => {
  const normalized = normalizePath(pathname)
  const stripped = normalized.replace(localePathPattern, '') || '/'
  return normalizePath(stripped)
}

const buildLocalizedPathname = (basePathname: string, locale: 'zh-CN' | 'en-US') => {
  const segment = pathSegmentByLocale[locale]
  const normalizedBase = normalizePath(basePathname)
  return normalizedBase === '/' ? `/${segment}` : `/${segment}${normalizedBase}`
}

const getInitialLocale = () => {
  const localeFromPath = getLocaleFromPathname(window.location.pathname)
  if (localeFromPath) {
    return localeFromPath
  }

  const savedLang = window.localStorage.getItem(storageKey)
  if (savedLang) {
    return toSupportedLocale(savedLang)
  }

  return 'zh-CN'
}

const upsertHeadLink = (selector: string, rel: string, href: string, hreflang?: string) => {
  let link = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
  if (hreflang) {
    link.hreflang = hreflang
  } else {
    link.removeAttribute('hreflang')
  }
}

const syncLanguageMeta = (locale: 'zh-CN' | 'en-US') => {
  const currentUrl = new URL(window.location.href)
  currentUrl.hash = ''
  currentUrl.search = ''
  const basePathname = getBasePathname(currentUrl.pathname)
  const pageTitle = getPageTitle(basePathname, locale)
  const pageDescription = getPageDescription(basePathname, locale)
  const pageKeywords = getPageKeywords(basePathname, locale)
  const zhUrl = new URL(currentUrl.toString())
  zhUrl.pathname = buildLocalizedPathname(basePathname, 'zh-CN')
  const enUrl = new URL(currentUrl.toString())
  enUrl.pathname = buildLocalizedPathname(basePathname, 'en-US')
  const canonicalUrl = locale === 'zh-CN' ? zhUrl : enUrl
  const toHref = (url: URL) => {
    const pathWithQuery = `${url.pathname}${url.search}`
    return siteOrigin ? `${siteOrigin}${pathWithQuery}` : pathWithQuery
  }

  upsertHeadLink('link[rel="canonical"]', 'canonical', toHref(canonicalUrl))
  upsertHeadLink('link[rel="alternate"][hreflang="zh-cn"]', 'alternate', toHref(zhUrl), 'zh-cn')
  upsertHeadLink('link[rel="alternate"][hreflang="en-us"]', 'alternate', toHref(enUrl), 'en-us')
  upsertHeadLink('link[rel="alternate"][hreflang="x-default"]', 'alternate', toHref(enUrl), 'x-default')
  document.title = `${pageTitle} | ${siteNameByLocale[locale]}`
  upsertHeadMeta('meta[name="description"]', 'description', pageDescription)
  upsertHeadMeta('meta[name="keywords"]', 'keywords', pageKeywords.join(', '))
}

const syncStructuredData = (locale: SupportedLocale) => {
  const currentUrl = new URL(window.location.href)
  currentUrl.hash = ''
  currentUrl.search = ''

  const basePathname = getBasePathname(currentUrl.pathname)
  const localizedPathname = buildLocalizedPathname(basePathname, locale)
  const currentPageUrl = toAbsoluteUrl(localizedPathname)
  const companyName = companyNameByLocale[locale]
  const siteName = siteNameByLocale[locale]
  const pageTitle = getPageTitle(basePathname, locale)
  const pageDescription = getPageDescription(basePathname, locale)
  const breadcrumbs = getBreadcrumbItems(basePathname, locale)

  const routeSchemas = buildRouteSchemas(basePathname, {
    locale,
    siteOrigin: defaultSiteOrigin,
    companyName,
    siteName,
    currentPageUrl,
    pageTitle
  })

  upsertJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${defaultSiteOrigin}/#organization`,
      name: companyName,
      alternateName: locale === 'zh-CN' ? companyNameByLocale['en-US'] : companyNameByLocale['zh-CN'],
      description: siteDescriptionByLocale[locale],
      url: defaultSiteOrigin,
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/shcdlogo.png')
      },
      image: toAbsoluteUrl('/shcdlogo.png'),
      email: 'sales@shsctec.com',
      telephone: '+86-21-50817399',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '781 Kangwei Road',
        addressLocality: 'Pudong New District',
        addressRegion: 'Shanghai',
        addressCountry: 'CN'
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'sales',
          telephone: '+86-21-64056173',
          email: 'sales@shsctec.com',
          availableLanguage: ['zh-CN', 'en-US']
        },
        {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'inquiry@shsctec.com',
          availableLanguage: ['zh-CN', 'en-US']
        },
        {
          '@type': 'ContactPoint',
          contactType: 'recruitment',
          email: 'hr@shsctec.com',
          availableLanguage: ['zh-CN', 'en-US']
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${defaultSiteOrigin}/#website`,
      url: defaultSiteOrigin,
      name: siteName,
      alternateName: companyName,
      inLanguage: ['zh-CN', 'en-US'],
      publisher: {
        '@id': `${defaultSiteOrigin}/#organization`
      },
      description: siteDescriptionByLocale[locale]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${currentPageUrl}#webpage`,
      url: currentPageUrl,
      name: `${pageTitle} | ${siteName}`,
      headline: pageTitle,
      description: pageDescription,
      inLanguage: locale,
      isPartOf: {
        '@id': `${defaultSiteOrigin}/#website`
      },
      about: {
        '@id': `${defaultSiteOrigin}/#organization`
      },
      breadcrumb: {
        '@id': `${currentPageUrl}#breadcrumb`
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${currentPageUrl}#breadcrumb`,
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.item
      }))
    },
    ...routeSchemas
  ])
}

const syncLanguage = (lang: string | null | undefined) => {
  const locale = toSupportedLocale(lang)
  if (langStore.currentLang !== locale) {
    langStore.setLang(locale)
  }
  if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale
  }

  const nextUrl = new URL(window.location.href)
  const basePathname = getBasePathname(nextUrl.pathname)
  const localizedPathname = buildLocalizedPathname(basePathname, locale)
  if (nextUrl.pathname !== localizedPathname) {
    nextUrl.pathname = localizedPathname
    window.history.replaceState({}, '', nextUrl.toString())
  }

  document.documentElement.lang = locale
  window.localStorage.setItem(storageKey, locale)
  syncLanguageMeta(locale)
  syncStructuredData(locale)
}

onMounted(() => {
  syncLanguage(getInitialLocale())
})

watch(
  () => route.fullPath,
  () => {
    const localeFromPath = getLocaleFromPathname(window.location.pathname)
    syncLanguage(localeFromPath ?? langStore.currentLang)
  }
)

watch(
  () => langStore.currentLang,
  (newLang: string) => {
    syncLanguage(newLang)
  },
  { immediate: true }
)

watch(
  () => i18n.global.locale.value,
  (newLang) => {
    syncLanguage(newLang)
  }
)
</script>

<template>
  <div class="app">
    <!-- 桌面端Header -->
    <Header class="desktop-only" />
    <!-- 移动端Header -->
    <MobileHeader class="mobile-only" />
    <router-view></router-view>
  </div>
</template>

<style lang="scss">
.app {
  min-height: 100vh;
  background: url('/images/背景1.jpg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(189, 215, 238, 0.6) 100%);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 0;
    backdrop-filter: blur(12px) saturate(180%);
  }

  > * {
    position: relative;
    z-index: 2;
  }
  
} 

// 显示控制类
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }
}

// 重置默认样式
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  line-height: 1.5;
}

a {
  text-decoration: none;
  color: inherit;
}

img {
  max-width: 100%;
  height: auto;
}
</style>
